export const DEFAULT_SCROLL_THRESHOLDS = [25, 50, 75, 90];

export function normalizePathname(pathname) {
  const value = String(pathname || '/').trim();
  if (!value) return '/';
  if (value === '/') return '/';
  const withLeadingSlash = value.startsWith('/') ? value : `/${value}`;
  return withLeadingSlash.replace(/\/{2,}/g, '/');
}

export function normalizeSearch(search) {
  const value = String(search || '').trim();
  if (!value) return '';
  return value.startsWith('?') ? value : `?${value}`;
}

export function buildRouteKey({ pathname = '/', search = '' } = {}) {
  return `${normalizePathname(pathname)}${normalizeSearch(search)}`;
}

export function createRouteChangeTracker(options) {
  const onPageView = options && typeof options.onPageView === 'function' ? options.onPageView : () => {};
  const now = options && typeof options.now === 'function' ? options.now : () => Date.now();
  const dedupeWindowMs = Number((options && options.dedupeWindowMs) || 1000);

  let lastRouteKey = '';
  let lastTrackedAt = 0;

  return {
    track(routeState) {
      const routeKey = buildRouteKey(routeState || {});
      const timestamp = Number(now());
      if (routeKey === lastRouteKey && timestamp - lastTrackedAt >= 0 && timestamp - lastTrackedAt < dedupeWindowMs) {
        return false;
      }

      lastRouteKey = routeKey;
      lastTrackedAt = timestamp;
      onPageView({ ...(routeState || {}), routeKey, trackedAt: timestamp });
      return true;
    },
    getLastRouteKey() {
      return lastRouteKey;
    },
  };
}

export function createScrollDepthTracker(options) {
  const thresholds = Array.from(new Set(
    (Array.isArray(options && options.thresholds) ? options.thresholds : DEFAULT_SCROLL_THRESHOLDS)
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0 && value <= 100),
  )).sort((a, b) => a - b);
  const onThreshold = options && typeof options.onThreshold === 'function' ? options.onThreshold : () => {};

  let currentPageKey = '';
  let fired = new Set();

  function reset(pageKey) {
    const nextKey = String(pageKey || '');
    currentPageKey = nextKey;
    fired = new Set();
  }

  function update(progressPercent, context) {
    const progress = Math.max(0, Math.min(100, Number(progressPercent) || 0));
    const newlyFired = [];

    for (const threshold of thresholds) {
      if (progress < threshold || fired.has(threshold)) continue;
      fired.add(threshold);
      const payload = {
        threshold,
        progressPercent: progress,
        pageKey: currentPageKey,
        ...(context || {}),
      };
      onThreshold(payload);
      newlyFired.push(threshold);
    }

    return newlyFired;
  }

  return {
    reset,
    update,
    getThresholds() {
      return thresholds.slice();
    },
    getFiredThresholds() {
      return Array.from(fired).sort((a, b) => a - b);
    },
    getCurrentPageKey() {
      return currentPageKey;
    },
  };
}

export function classifyLinkTarget(input) {
  const href = String((input && input.href) || '').trim();
  if (!href) return null;

  if (/^(javascript:|#)/i.test(href)) return null;
  if (/^mailto:/i.test(href)) {
    return { eventName: 'email_click', kind: 'mailto', href };
  }
  if (/^tel:/i.test(href)) {
    return { eventName: 'click_to_call', kind: 'tel', href };
  }
  if (/^(whatsapp:|https?:\/\/(?:wa\.me|api\.whatsapp\.com)\/)/i.test(href)) {
    return { eventName: 'whatsapp_click', kind: 'whatsapp', href };
  }

  if (!/^https?:/i.test(href)) return null;

  let parsed = null;
  try {
    parsed = new URL(href);
  } catch (_err) {
    return null;
  }

  const currentOrigin = String((input && input.currentOrigin) || '').trim();
  if (currentOrigin && parsed.origin === currentOrigin) {
    return null;
  }

  return {
    eventName: 'outbound_click',
    kind: 'outbound',
    href,
    host: parsed.host,
    protocol: parsed.protocol,
  };
}

export function classifyFormDescriptor(input) {
  const pathname = normalizePathname(input && input.pathname);
  const formId = String((input && input.formId) || '').trim();
  const formName = String((input && input.formName) || '').trim();
  const className = String((input && input.className) || '').trim();
  const fieldNames = new Set(Array.isArray(input && input.fieldNames) ? input.fieldNames.map((name) => String(name || '').trim().toLowerCase()) : []);

  const signature = `${formId} ${formName} ${className}`.toLowerCase();
  const hasLeadFields = fieldNames.has('phone') || fieldNames.has('email');

  if (pathname === '/contact' || signature.includes('contact')) {
    return { eventName: 'generate_lead', formType: 'contact', leadIntent: 'contact' };
  }

  if (pathname === '/' && hasLeadFields) {
    return { eventName: 'generate_lead', formType: 'lead', leadIntent: 'landing' };
  }

  if (hasLeadFields) {
    return { eventName: 'generate_lead', formType: 'lead', leadIntent: 'generic' };
  }

  return { eventName: 'form_submit', formType: 'generic', leadIntent: '' };
}

export default {
  DEFAULT_SCROLL_THRESHOLDS,
  buildRouteKey,
  classifyFormDescriptor,
  classifyLinkTarget,
  createRouteChangeTracker,
  createScrollDepthTracker,
  normalizePathname,
  normalizeSearch,
};
